import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { m } from 'framer-motion';
import Header from './Header';
import Footer from './Footer';
import CookieBanner from './CookieBanner';
import { SITE_ORIGIN, internalUrl } from './siteConfig';
import { groupAreasByCounty, LOCATIONS_HUB_COUNTY_ORDER } from './localAreasData';
import { META_LOCATIONS_HUB, applyPageMeta } from './seoMeta';
import { organizationWithReviewsNode } from './organizationSchema';

const CANONICAL = META_LOCATIONS_HUB.canonical;
const SCHEMA_ID = 'webcodecraft-locations-hub-schema';

function webDesignAnchorText(areaName) {
  return `Web design in ${areaName}`;
}

function LocationsHubPage() {
  const grouped = useMemo(() => groupAreasByCounty(), []);
  const jsonLd = useMemo(
    () =>
      JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': [
          organizationWithReviewsNode(),
          {
            '@type': 'WebPage',
            '@id': `${CANONICAL}#webpage`,
            url: CANONICAL,
            name: META_LOCATIONS_HUB.title,
            description: META_LOCATIONS_HUB.description,
            isPartOf: { '@type': 'WebSite', name: 'WebCodeCraft', url: SITE_ORIGIN },
            inLanguage: 'en-GB'
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
              { '@type': 'ListItem', position: 2, name: 'Areas we serve', item: CANONICAL }
            ]
          }
        ]
      }),
    []
  );

  useEffect(() => {
    const restoreMeta = applyPageMeta(META_LOCATIONS_HUB);

    const existing = document.getElementById(SCHEMA_ID);
    if (!existing) {
      const el = document.createElement('script');
      el.id = SCHEMA_ID;
      el.type = 'application/ld+json';
      el.textContent = jsonLd;
      document.head.appendChild(el);
    }

    return () => {
      restoreMeta();
      const script = document.getElementById(SCHEMA_ID);
      if (script) script.remove();
    };
  }, [jsonLd]);

  return (
    <>
      <Header />
      <main
        id="main-content"
        className="pt-24 pb-16 min-w-0 overflow-x-clip bg-gradient-to-b from-gray-50 to-white min-h-screen"
      >
        <div className="container mx-auto px-4 max-w-4xl">
          <m.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            <p className="text-sm text-indigo-600 font-medium mb-4">
              <Link to="/" className="hover:underline">
                Home
              </Link>
              <span className="mx-2 text-gray-400" aria-hidden="true">
                /
              </span>
              <span className="text-gray-600">Areas we serve</span>
            </p>

            <div className="mb-6 lg:hidden">
              <a
                href={internalUrl('contact')}
                className="inline-flex w-full sm:w-auto min-h-[48px] items-center justify-center px-6 py-3.5 rounded-xl bg-indigo-600 text-white font-semibold shadow-md hover:bg-indigo-700 transition-colors"
              >
                Request a quote
              </a>
            </div>

            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              Website design &amp; monthly web builds — Devon, Cornwall &amp; UK cities
            </h1>
            <p className="text-lg text-gray-600 mb-10 leading-relaxed max-w-3xl">
              Pay monthly websites without the upfront cost across Devon, Cornwall, the wider South West, and selected UK cities.
              WebCodeCraft is South West based with UK-wide delivery; each area page explains bespoke sites, SSL, hosting, and technical SEO on a monthly plan.
              Pick your area below or{' '}
              <a href={internalUrl('contact')} className="text-indigo-600 font-medium hover:underline">
                start a project
              </a>
              .
            </p>

            {LOCATIONS_HUB_COUNTY_ORDER.filter((county) => (grouped[county] || []).length > 0).map((county) => (
              <section key={county} className="mb-12" aria-labelledby={`county-${county}`}>
                <h2 id={`county-${county}`} className="text-2xl font-bold text-gray-900 mb-4">
                  {county}
                </h2>
                <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 list-none p-0">
                  {grouped[county].map((area) => (
                    <li key={area.slug}>
                      <Link
                        to={`/locations/${area.slug}`}
                        className="block rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-medium text-indigo-700 hover:border-indigo-300 hover:bg-indigo-50/60 transition-colors leading-snug"
                      >
                        {webDesignAnchorText(area.name)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            ))}

            <p className="text-sm text-gray-500 mt-10">
              <a href={internalUrl('faq')} className="text-indigo-600 hover:underline">
                FAQ
              </a>
              {' · '}
              <a href={internalUrl('services')} className="text-indigo-600 hover:underline">
                Packages &amp; pricing
              </a>
            </p>
          </m.div>
        </div>
      </main>
      <Footer />
      <CookieBanner />
    </>
  );
}

export default LocationsHubPage;
